<script setup lang="ts">
import { computed } from 'vue';

import { EntityPresenterMode } from '@satellite/composables/presenterEntityMode';
import { useYardAreaFetcher } from '@satellite/composables/useYardAreaFetcher';
import { useYardAreaDisplay } from '@satellite/composables/useYardAreaDisplay';
import YardAreaDisplay from '@satellite/components/elements/presenters/YardAreaDisplay.vue';

import type { Maybe } from '@nova/core';
import type { PresenterPlaceholderData } from '@satellite/composables/presenterPlaceholder';
import type { YardAreaDisplayField } from '@satellite/composables/useYardAreaDisplay';
import type { YardAreaDetails } from '@satellite/composables/yardAreaPresenterShared';

const {
  size = 'regular',
  areaId = null,
  initialAreaData = null,
  placeholderAreaData = null,
  staticAreaData = null,
  mode = EntityPresenterMode.Live,
  displayFields = ['name'],
  nameProps = {}
} = defineProps<{
  size?: 'regular' | 'small' | 'large';
  areaId?: Maybe<string>;
  initialAreaData?: Maybe<YardAreaDetails>;
  placeholderAreaData?: Maybe<PresenterPlaceholderData<YardAreaDetails>>;
  staticAreaData?: Maybe<YardAreaDetails>;
  mode?: EntityPresenterMode;
  displayFields?: YardAreaDisplayField[];
  nameProps?: Record<string, unknown>;
}>();

const {
  data: area,
  isLoading,
  isFetching,
  onIntersect
} = useYardAreaFetcher({
  areaId: computed(() => areaId),
  initialAreaData: computed(() => initialAreaData),
  placeholderAreaData: computed(() => placeholderAreaData),
  staticAreaData: computed(() => staticAreaData),
  mode
});

const data = useYardAreaDisplay(area, {
  size: computed(() => size),
  mode: computed(() => mode),
  isFetching,
  nameProps: computed(() => nameProps)
});
</script>

<template>
  <div
    v-intersect="{
      handler: onIntersect,
      options: {
        threshold: [0.5]
      }
    }">
    <v-skeleton-loader :loading="isLoading" type="text">
      <yard-area-display
        class="d-flex flex-column gap-1 yard-area-presenter"
        :data="data"
        :display-fields="displayFields"
        :style="isLoading ? 'min-width: 50px' : ''">
        <template v-for="(_, slotName) in $slots" #[slotName]="slotProps">
          <slot :name="slotName" v-bind="slotProps ?? {}" />
        </template>
      </yard-area-display>
    </v-skeleton-loader>
  </div>
</template>

<style scoped>
:deep(.v-skeleton-loader__text) {
  margin: 0 auto !important;
  height: 1em !important;
}
</style>
